import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Loader2, Plus, Trash2, UploadCloud, Video } from "lucide-react";
import { toast } from "sonner";
import { API, formatApiErrorDetail, useAuth } from "../../context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export default function Exercises() {
  const { user } = useAuth();
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/exercises`);
      setExercises(data);
    } catch {
      toast.error("Übungen konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!exercises.some((e) => e.status === "processing")) return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [exercises, load]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Bitte eine Videodatei auswählen");
      return;
    }
    setUploading(true);
    setProgress(0);
    try {
      const formData = new FormData();
      formData.append("title", title);
      formData.append("description", description);
      formData.append("file", file);
      await axios.post(`${API}/exercises`, formData, {
        onUploadProgress: (e) => setProgress(Math.round((e.loaded * 100) / (e.total || 1))),
      });
      toast.success("Übung hochgeladen – Video wird verarbeitet…");
      setDialogOpen(false);
      setTitle("");
      setDescription("");
      setFile(null);
      setProgress(0);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (exerciseId) => {
    try {
      await axios.delete(`${API}/exercises/${exerciseId}`);
      toast.success("Übung gelöscht");
      setExercises((prev) => prev.filter((e) => e.exercise_id !== exerciseId));
    } catch {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  return (
    <div className="space-y-8" data-testid="exercises-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-zinc-400">
            Übungsbibliothek
          </p>
          <h1 className="mt-1 font-heading text-3xl font-bold tracking-tight text-zinc-100 sm:text-4xl">
            Übungsvideos
          </h1>
          <p className="mt-2 text-base text-zinc-400">
            Lade Videos hoch und verwende sie in Trainingsplänen.
          </p>
        </div>
        <Button
          onClick={() => setDialogOpen(true)}
          className="bg-primary hover:bg-primary/90"
          data-testid="upload-exercise-button"
        >
          <Plus className="mr-2 h-4 w-4" />
          Neue Übung
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20" data-testid="exercises-loading">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : exercises.length === 0 ? (
        <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-12 text-center" data-testid="no-exercises-message">
          <Video className="mx-auto mb-3 h-10 w-10 text-zinc-600" />
          <p className="text-zinc-400">
            Noch keine Übungen. Lade dein erstes Übungsvideo hoch.
          </p>
        </div>
      ) : (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="exercises-grid">
          {exercises.map((ex) => (
            <div
              key={ex.exercise_id}
              className="overflow-hidden rounded-xl border border-zinc-800 bg-zinc-900 shadow-sm"
              data-testid={`exercise-card-${ex.exercise_id}`}
            >
              {ex.status === "processing" ? (
                <div
                  className="flex aspect-video w-full flex-col items-center justify-center gap-2 bg-zinc-900"
                  data-testid={`exercise-processing-${ex.exercise_id}`}
                >
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                  <span className="text-sm text-zinc-400">Video wird verarbeitet…</span>
                </div>
              ) : ex.status === "error" ? (
                <div
                  className="flex aspect-video w-full items-center justify-center bg-zinc-900 text-sm text-destructive"
                  data-testid={`exercise-error-${ex.exercise_id}`}
                >
                  Verarbeitung fehlgeschlagen
                </div>
              ) : (
                <video
                  controls
                  preload="metadata"
                  poster={`${API}/exercises/${ex.exercise_id}/poster`}
                  className="aspect-video w-full bg-zinc-900 object-contain"
                  src={`${API}/videos/${ex.video_path}`}
                  data-testid={`exercise-video-${ex.exercise_id}`}
                />
              )}
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-semibold text-zinc-100">{ex.title}</p>
                    {ex.description && (
                      <p className="mt-1 line-clamp-2 text-sm text-zinc-400">{ex.description}</p>
                    )}
                  </div>
                  {(ex.is_owner || user?.is_admin) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(ex.exercise_id)}
                      className="text-zinc-500 hover:text-destructive"
                      data-testid={`delete-exercise-button-${ex.exercise_id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="upload-exercise-dialog">
          <DialogHeader>
            <DialogTitle>Neue Übung hochladen</DialogTitle>
            <DialogDescription>Video-Datei auswählen und Titel vergeben.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpload} className="space-y-4" data-testid="upload-exercise-form">
            <div className="space-y-2">
              <Label htmlFor="ex-title">Titel</Label>
              <Input
                id="ex-title"
                required
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="z. B. Kniebeuge mit Theraband"
                data-testid="exercise-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ex-desc">Beschreibung (optional)</Label>
              <Textarea
                id="ex-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Ausführung, Hinweise, häufige Fehler…"
                rows={3}
                data-testid="exercise-description-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ex-file">Videodatei (mp4, webm, mov, 3gp – max. 250 MB)</Label>
              <Input
                id="ex-file"
                ref={fileRef}
                type="file"
                accept="video/*"
                required
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="exercise-video-input"
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-primary hover:bg-primary/90"
              disabled={uploading}
              data-testid="exercise-upload-submit-button"
            >
              {uploading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <UploadCloud className="mr-2 h-4 w-4" />
              )}
              {uploading ? `Wird hochgeladen… ${progress}%` : "Hochladen"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
